import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { isTodayRome, getMsUntilMidnightRome } from '@/lib/utils';

const PushNotificationContext = createContext(null);

const MAX_PUSH_NOTIFICATIONS = 3;

export function PushNotificationProvider({ children }) {
  const [pushNotifications, setPushNotifications] = useState([]);
  const midnightTimeoutRef = useRef(null);

  // Add a new push notification
  const addPushNotification = useCallback((notification) => {
    // Only add if notification is from today (Rome timezone)
    if (!notification.created_at || !isTodayRome(notification.created_at)) {
      console.log('[PushNotification] Ignored notification from previous day:', notification.id);
      return;
    }

    setPushNotifications(prev => {
      // Add new notification at the beginning
      const next = [notification, ...prev];
      
      // Deduplicate by id
      const uniqueById = next.filter((n, index, self) => 
        index === self.findIndex(t => t.id === n.id)
      );
      
      // Filter only today's notifications (safety)
      const todayOnly = uniqueById.filter(n => isTodayRome(n.created_at));
      
      // Sort by created_at descending (most recent first)
      const sorted = todayOnly.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      // Limit to MAX_PUSH_NOTIFICATIONS
      return sorted.slice(0, MAX_PUSH_NOTIFICATIONS);
    });
  }, []);

  // Remove a specific push notification
  const removePushNotification = useCallback((notificationId) => {
    setPushNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Clear all push notifications
  const clearPushNotifications = useCallback(() => {
    setPushNotifications([]);
  }, []);

  // Get filtered and sorted notifications for rendering
  const getDisplayNotifications = useCallback(() => {
    return pushNotifications
      .filter(n => isTodayRome(n.created_at))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, MAX_PUSH_NOTIFICATIONS);
  }, [pushNotifications]);

  // Midnight reset: clear all push notifications at midnight (Europe/Rome)
  useEffect(() => {
    const scheduleMidnightReset = () => {
      const msUntilMidnight = getMsUntilMidnightRome();
      console.log(`[PushNotification] Scheduling midnight reset in ${Math.round(msUntilMidnight / 1000 / 60)} minutes`);
      
      midnightTimeoutRef.current = setTimeout(() => {
        console.log('[PushNotification] Midnight reset triggered - clearing all push notifications');
        clearPushNotifications();
        // Schedule next midnight reset
        scheduleMidnightReset();
      }, msUntilMidnight);
    };

    scheduleMidnightReset();

    return () => {
      if (midnightTimeoutRef.current) {
        clearTimeout(midnightTimeoutRef.current);
      }
    };
  }, [clearPushNotifications]);

  const value = {
    pushNotifications,
    addPushNotification,
    removePushNotification,
    clearPushNotifications,
    getDisplayNotifications,
    MAX_PUSH_NOTIFICATIONS
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotifications() {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider');
  }
  return context;
}
